/** Player settings persisted to localStorage. Bump VERSION on schema changes
 * and extend `migrate` — saved settings must survive updates. */

export type Quality = 'low' | 'medium' | 'high' | 'ultra';

export interface Settings {
  version: 1;
  /** Mouse sensitivity, radians per pixel. */
  sensitivity: number;
  /** Vertical-agnostic FOV in degrees (Three.js camera fov). */
  fov: number;
  /** Master volume 0-1. */
  volume: number;
  /** Graphics quality preset (post-processing + shadow detail). */
  quality: Quality;
  /** Hold-jump bunnyhopping: true = hop while Space is held; false = re-press per jump. */
  autoBhop: boolean;
  /** Selected hero id (see src/characters/roster.ts). Unknown ids fall back to the default. */
  selectedCharacter: string;
}

export const DEFAULT_SETTINGS: Settings = {
  version: 1,
  sensitivity: 0.0022,
  fov: 90,
  volume: 0.8,
  // Default to the lightest path — smooth in any browser; players with a strong
  // GPU can opt into medium/high/ultra in Settings.
  quality: 'low',
  // Auto-bhop on by default — the forgiving surf-server standard.
  autoBhop: true,
  // Vaelith is the only active hero for now (see src/characters/roster.ts).
  selectedCharacter: 'vaelith',
};

const STORAGE_KEY = 'fall-line.settings';

/** Parses raw persisted JSON into valid Settings, falling back per-field. */
export function migrate(raw: unknown): Settings {
  const out = { ...DEFAULT_SETTINGS };
  if (typeof raw === 'object' && raw !== null) {
    const r = raw as Record<string, unknown>;
    if (typeof r.sensitivity === 'number' && r.sensitivity > 0 && r.sensitivity < 0.1) {
      out.sensitivity = r.sensitivity;
    }
    if (typeof r.fov === 'number' && r.fov >= 60 && r.fov <= 130) {
      out.fov = r.fov;
    }
    if (typeof r.volume === 'number' && r.volume >= 0 && r.volume <= 1) {
      out.volume = r.volume;
    }
    if (
      r.quality === 'low' ||
      r.quality === 'medium' ||
      r.quality === 'high' ||
      r.quality === 'ultra'
    ) {
      out.quality = r.quality;
    }
    if (typeof r.autoBhop === 'boolean') {
      out.autoBhop = r.autoBhop;
    }
    // Stored loosely; an unknown id is resolved to the default at load time.
    if (typeof r.selectedCharacter === 'string' && r.selectedCharacter !== '') {
      out.selectedCharacter = r.selectedCharacter;
    }
  }
  return out;
}

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return migrate(raw ? JSON.parse(raw) : null);
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Storage unavailable (private mode etc.) — settings just won't persist.
  }
}
